import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Stack,
  useTheme,
  Drawer,
  IconButton,
  Tooltip,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useQueryClient } from '@tanstack/react-query';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { hasInternalDashAccessState, searchHandlerState } from 'states';
import { SearchHandlerFunction } from 'types/shared';
import { ExtendedStructureRequest, MoviefoneDetailView } from 'types/moviefone';
import { dayjs } from 'util/shared/date';

import MoviefoneChat, { MoviefoneChatHandle } from './MoviefoneChat';
import MoviefoneNotesDrawer from './MoviefoneNotesDrawer';
import MoviefonePreviousThreads from './MoviefonePreviousThreads';
import MoviefoneStrategyCard from './MoviefoneStrategyCard';
import MoviefoneCompareView from './MoviefoneCompareView';
import {
  ApiError,
  SearchCandidate,
  DefaultService as StreamAPI,
  MoviefoneAnalysis,
  MoviefoneConversationSummary,
  StructureRequest,
} from 'gen/stream-api/requests';
import useRiskFreeRate from 'hooks/riskFreeRate/useRiskFreeRate';
import useIsMobile from 'hooks/useIsMobile';

const STRATEGY_CARD_IS_DRAWER_AT_CONTAINER_BREAK_EX = 160;

const MoviefoneContainer = () => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const isInternalUser = useRecoilValue(hasInternalDashAccessState);
  const [tradeCriteria, setTradeCriteria] = useState<ExtendedStructureRequest>(
    {},
  );
  const sym = tradeCriteria.symbol ?? undefined;
  const setSym = useCallback(
    (symbol: string) => setTradeCriteria((prev) => ({ ...prev, symbol })),
    [setTradeCriteria],
  );
  const setSuggestionsHandler = useSetRecoilState(searchHandlerState);
  const [notesDrawerOpen, setNotesDrawerOpen] = useState(false);
  const chatRef = useRef<MoviefoneChatHandle>(null);
  const queryClient = useQueryClient();
  const [detailView, setDetailView] = useState<MoviefoneDetailView | null>(
    null,
  );

  const { getRiskFreeRateForYte } = useRiskFreeRate(dayjs());
  const riskFreeRate = getRiskFreeRateForYte?.(1, true);

  const [_error, setError] = useState<ApiError | null>(null);
  const [moviefoneAnalysis, setMoviefoneAnalysis] =
    useState<MoviefoneAnalysis | null>(null);

  const [previousConversations, setPreviousConversations] = useState<
    MoviefoneConversationSummary[]
  >([]);
  const [isLoadingPreviousConversations, setIsLoadingPreviousConversations] =
    useState(false);
  const [activeConversationTimestamp, setActiveConversationTimestampState] =
    useState<string | null>(null);
  // Mirrored into a ref so onConversationListChanged can check the *current* active
  // timestamp synchronously - React state wouldn't reflect it until the next render.
  const activeConversationTimestampRef = useRef<string | null>(null);
  const setActiveConversationTimestamp = useCallback(
    (timestamp: string | null) => {
      activeConversationTimestampRef.current = timestamp;
      setActiveConversationTimestampState(timestamp);
    },
    [],
  );
  // True from the moment a brand-new conversation's first message is sent until
  // its auto-save completes, so the sidebar can show an immediate placeholder
  // rather than waiting on the full round-trip.
  const [isPendingNewConversation, setIsPendingNewConversation] =
    useState(false);
  // Conversations that finished saving something new while they weren't the one on screen -
  // e.g. a response completed after the user switched away. Cleared once the user opens
  // that conversation.
  const [unreadTimestamps, setUnreadTimestamps] = useState<Set<string>>(
    new Set(),
  );

  const hasLoadedConversationsOnceRef = useRef(false);
  const refreshPreviousConversations = useCallback(async () => {
    // Only show the loading spinner (which replaces the whole list) on the very first fetch -
    // a background refresh after a save should swap the list in place, not flash it away.
    const isInitialLoad = !hasLoadedConversationsOnceRef.current;
    if (isInitialLoad) {
      setIsLoadingPreviousConversations(true);
    }
    try {
      const list = await StreamAPI.getV1MoviefoneConversations();
      setPreviousConversations(list);
      return list;
    } catch (error) {
      console.error('Failed to list saved conversations', error);
      setPreviousConversations([]);
      return [];
    } finally {
      hasLoadedConversationsOnceRef.current = true;
      if (isInitialLoad) {
        setIsLoadingPreviousConversations(false);
      }
    }
  }, []);

  useEffect(() => {
    void refreshPreviousConversations();
  }, [refreshPreviousConversations]);

  const prevFetchMoviefoneAnalysisRef =
    useRef<Promise<null | MoviefoneAnalysis> | null>(null);
  const fetchMoviefoneAnalysis = useCallback(
    async ({
      riskFreeRate,
      tradeCriteria,
    }: {
      riskFreeRate?: number;
      tradeCriteria: StructureRequest;
    }): Promise<MoviefoneAnalysis | null> => {
      const { promise: thisFetchPromise, resolve: thisFetchResolve } =
        Promise.withResolvers<null | MoviefoneAnalysis>();
      // Claim the slot before awaiting, so concurrent callers form a queue rather than
      // all awaiting the same predecessor and then running at once.
      const prevFetch = prevFetchMoviefoneAnalysisRef.current;
      prevFetchMoviefoneAnalysisRef.current = thisFetchPromise;
      if (prevFetch != null) {
        await prevFetch;
      }
      if (tradeCriteria.symbol == null || riskFreeRate == null) {
        setMoviefoneAnalysis(null);
        thisFetchResolve(null);
        return null;
      }
      try {
        console.log('tradeCriteria', JSON.stringify(tradeCriteria));
        const moviefoneAnalysis = await queryClient.fetchQuery({
          queryKey: ['DefaultServicePostV1SpotlineAnalysis', tradeCriteria],
          queryFn: () =>
            StreamAPI.postV1SpotlineAnalysis({
              riskFreeRate,
              requestBody: tradeCriteria,
            }),
        });
        setMoviefoneAnalysis(moviefoneAnalysis);
        thisFetchResolve(moviefoneAnalysis);
        return moviefoneAnalysis;
      } catch (e) {
        console.error(e);
        setError(e as ApiError);
      }
      thisFetchResolve(null);
      return null;
    },
    [setError, setMoviefoneAnalysis, queryClient],
  );

  useEffect(() => {
    const handler: SearchHandlerFunction = (val: string): void => {
      const s = val.toUpperCase();
      if (s) {
        setSym(s);
      }
    };
    setSuggestionsHandler(() => handler);
  }, [setSym, setSuggestionsHandler]);

  return (
    <>
      <Stack
        sx={{
          // Layout drops its own height on mobile (most pages there just page-scroll) - self-
          // sufficient here instead, matching TapeContainer/HiroBottomQuadrantTabs.
          height: isMobile ? 'calc(100vh - 64px)' : '100%',
          overflow: 'hidden',
          backgroundColor: theme.palette.background.default,
          position: 'relative',
        }}
      >
        {isInternalUser && (
          <Tooltip title="Notes" placement="right">
            <IconButton
              aria-label="open notes"
              data-ph-capture-attribute-element="moviefone-notes-toggle"
              size="small"
              onClick={() => setNotesDrawerOpen((prev) => !prev)}
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                zIndex: 1,
                color: theme.palette.sgGreen,
                backgroundColor: theme.palette.background.default,
                '&:hover': {
                  backgroundColor: theme.palette.background.default,
                },
              }}
            >
              <EditNoteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {/* ── Content ── */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flex: 1,
            overflow: 'hidden',
          }}
        >
          {/* Previous conversations drawer */}
          <MoviefonePreviousThreads
            conversations={previousConversations}
            isLoading={isLoadingPreviousConversations}
            isPendingNewConversation={isPendingNewConversation}
            activeTimestamp={activeConversationTimestamp}
            unreadTimestamps={unreadTimestamps}
            onSelectConversation={(timestamp) => {
              const wasUnread = unreadTimestamps.has(timestamp);
              setActiveConversationTimestamp(timestamp);
              setIsPendingNewConversation(false);
              // A selected trade card (or compare view) belongs to whatever
              // conversation was showing - not the newly-loaded one.
              setDetailView(null);
              setUnreadTimestamps((prev) => {
                if (!prev.has(timestamp)) {
                  return prev;
                }
                const next = new Set(prev);
                next.delete(timestamp);
                return next;
              });
              chatRef.current?.loadConversation(timestamp, wasUnread);
            }}
            onNewConversation={() => {
              setActiveConversationTimestamp(null);
              setIsPendingNewConversation(false);
              setDetailView(null);
              chatRef.current?.startNewConversation();
            }}
            onDeleteConversation={(timestamp) => {
              StreamAPI.deleteV1MoviefoneConversationsByTimestamp({
                timestamp,
              })
                .then(() => {
                  setPreviousConversations((prev) =>
                    prev.filter((c) => c.timestamp !== timestamp),
                  );
                  setUnreadTimestamps((prev) => {
                    if (!prev.has(timestamp)) {
                      return prev;
                    }
                    const next = new Set(prev);
                    next.delete(timestamp);
                    return next;
                  });
                  if (timestamp === activeConversationTimestamp) {
                    setActiveConversationTimestamp(null);
                    setDetailView(null);
                    chatRef.current?.startNewConversation();
                  }
                })
                .catch((error: unknown) => {
                  console.error('Failed to delete conversation', error);
                });
            }}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flex: 1,
              overflow: 'hidden',
            }}
          >
            {/* Chat Panel */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: detailView != null ? 2 : 1,
                flexBasis: 0,
                minWidth: 0,
                overflow: 'hidden',
                backgroundColor: theme.palette.background.default,
                position: 'relative',
              }}
            >
              <MoviefoneChat
                tradeCriteria={tradeCriteria}
                setTradeCriteria={setTradeCriteria}
                ref={chatRef}
                moviefoneAnalysis={moviefoneAnalysis}
                setMoviefoneAnalysis={setMoviefoneAnalysis}
                fetchMoviefoneAnalysis={fetchMoviefoneAnalysis}
                prevFetchMoviefoneAnalysisRef={prevFetchMoviefoneAnalysisRef}
                detailView={detailView}
                onSelectStrategy={(
                  moviefoneAnalysis: MoviefoneAnalysis,
                  searchCandidate: SearchCandidate,
                ) => {
                  setMoviefoneAnalysis(moviefoneAnalysis);
                  setDetailView({ kind: 'SearchCandidate', searchCandidate });
                }}
                onSelectCompareMode={(
                  moviefoneAnalysis: MoviefoneAnalysis,
                  strategies: SearchCandidate[],
                ) => {
                  setMoviefoneAnalysis(moviefoneAnalysis);
                  setDetailView({ kind: 'compare', strategies });
                }}
                riskFreeRate={riskFreeRate}
                sym={sym}
                onNewConversationStarted={() =>
                  setIsPendingNewConversation(true)
                }
                onConversationSaved={(summary) => {
                  setActiveConversationTimestamp(summary.timestamp);
                  // Keep the skeleton up until the refreshed list actually contains this
                  // conversation, so it fills in directly rather than leaving a gap.
                  void refreshPreviousConversations().then(() => {
                    setIsPendingNewConversation(false);
                  });
                }}
                onConversationListChanged={(summary) => {
                  void refreshPreviousConversations();
                  if (
                    summary.timestamp !== activeConversationTimestampRef.current
                  ) {
                    setUnreadTimestamps((prev) =>
                      new Set(prev).add(summary.timestamp),
                    );
                  }
                }}
                onConversationFailed={() => setIsPendingNewConversation(false)}
              />
            </Box>

            {/* Right Panel */}
            {detailView != null && moviefoneAnalysis != null && (
              <Box
                sx={{
                  flexGrow: 3,
                  flexBasis: 0,
                  minWidth: 0,
                  overflow: 'hidden',
                  backgroundColor: theme.palette.background.default,
                  [`@media all and (max-width: ${STRATEGY_CARD_IS_DRAWER_AT_CONTAINER_BREAK_EX}ex)`]:
                    {
                      display: 'none',
                    },
                }}
              >
                {detailView.kind === 'SearchCandidate' ? (
                  <MoviefoneStrategyCard
                    searchCandidate={detailView.searchCandidate}
                    data={moviefoneAnalysis}
                    onAsk={(msg) => chatRef.current?.sendMessage(msg)}
                    onClose={() => setDetailView(null)}
                  />
                ) : (
                  <MoviefoneCompareView
                    strategies={detailView.strategies}
                    moviefoneAnalysis={moviefoneAnalysis}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
        <MoviefoneNotesDrawer
          open={notesDrawerOpen}
          onClose={() => setNotesDrawerOpen(false)}
        />
        {detailView != null && moviefoneAnalysis != null && (
          <Drawer
            open={true}
            anchor="right"
            onClose={() => setDetailView(null)}
            sx={{
              [`@media all and (min-width: ${STRATEGY_CARD_IS_DRAWER_AT_CONTAINER_BREAK_EX}ex)`]:
                {
                  display: 'none',
                },
            }}
          >
            <Box
              sx={{
                maxWidth: '85vw',
                overflow: 'hidden',
                backgroundColor: theme.palette.background.default,
              }}
            >
              {detailView.kind === 'SearchCandidate' ? (
                <MoviefoneStrategyCard
                  searchCandidate={detailView.searchCandidate}
                  data={moviefoneAnalysis}
                  onAsk={(msg) => chatRef.current?.sendMessage(msg)}
                  onClose={() => setDetailView(null)}
                />
              ) : (
                <MoviefoneCompareView
                  strategies={detailView.strategies}
                  moviefoneAnalysis={moviefoneAnalysis}
                />
              )}
            </Box>
          </Drawer>
        )}
      </Stack>
    </>
  );
};

export default MoviefoneContainer;
